import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  FeedbackStepper, Header, Tooltip,
} from '@/pages/common';
import { Button, IconArrow } from 'gatorcomponents';
import { CancelSurvey } from '../CancelSubscriptionHandler/CancelSurvey';
import { Confirmation, OrderProcessing } from '@/components/billing';
import Loader from '@/components/Layout/Loader';
import { SubscriptionsQuestionTypes } from '@/redux/modules/billing/billing.types';
import { billingActions, preChatActions } from '@/redux/modules';
import { formatDate } from '@/utils/Formatters/formatDate';
import useLocale from '@/hooks/useLocale';
import { billingAnalytics } from '@/analytics';
import * as Styles from './CancelSubscriptionRenewalHandler.styles';
import createSubscriptionCancelRenewalHandler from './CancelSubscriptionRenewalHandler.functions';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { XRAY_ENABLED_RETENTION_OFFER } from '@/config/GrowthBook/constants';
import { RetentionOfferPage } from '@/pages/billing/subscriptions/RetentionOfferPage';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';

const STEPS_STATUS = {
  SURVEY: 1,
  CONFIRMATION: 2,
  DONE: 3,
  NEXT_RENEW_OFFER: 4,
};

const CancelSubscriptionRenewalHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { billing: billingLocale, routes: routesLocale } = useLocale();
  const { subscriptions: subscriptionsLocale } = billingLocale;
  const { cancel: cancelLocale } = subscriptionsLocale;
  const {
    confirmation: confirmationLocale,
    feedbackStepper: feedbackStepperLocale,
  } = cancelLocale;

  const { subscriptionId, subscriptionType } = useParams();

  const loading = useSelector(state => state.common.loading);
  const redirect = useSelector(state => state.common.redirect);
  const subscriptions = useSelector(state => state.billing.subscriptions.items);
  const questions = useSelector(state => state.billing.subscriptions.questions);
  const cancel = useSelector(state => state.billing.subscriptions.cancel);
  const { triage } = useSelector(state => state.preChat);
  const [subscription, setSubscription] = useState(undefined);
  const enabledRetentionOffer = useFeatureIsOn(XRAY_ENABLED_RETENTION_OFFER);
  const { subscriptions: subscriptionAnalytics } = billingAnalytics;

  const openPreChatCancelFlow = useCallback(() => {
    if (triage.subscriptionToCancel) {
      dispatch(preChatActions.preChat.setShowPreChat(true));
      dispatch(preChatActions.subscription.setSubscriptionToCancel(null));
    }
  }, [triage, dispatch]);

  const canShowBackupLink = () => {
    if (subscription && subscription.showFilesExclusionText !== undefined) {
      return subscription.showFilesExclusionText;
    }
    return subscriptionType !== 'addon' && subscriptionType !== 'domain';
  };

  const {
    questionId, optionId, reason, modal,
  } = cancel;

  const steps = [
    {
      id: STEPS_STATUS.SURVEY,
      label: feedbackStepperLocale.stepSurveyLabel,
    },
    {
      id: STEPS_STATUS.CONFIRMATION,
      label: feedbackStepperLocale.stepConfirmationLabel,
    },
  ];

  const [step, setStep] = useState(STEPS_STATUS.SURVEY);

  const verifyParamsInUrl = useCallback(() => {
    if (enabledRetentionOffer) {
      const containsStep = window.location.href.includes('step=');
      const urlStep = window.location.href.includes('step=2') ? 2 : 1;
      const shouldOpenInvoice = containsStep && urlStep === 2;

      if (shouldOpenInvoice) {
        setStep(STEPS_STATUS.CONFIRMATION);
      }
    }
  }, [setStep, enabledRetentionOffer]);

  useEffect(() => {
    verifyParamsInUrl();
  }, [verifyParamsInUrl]);

  const subscriptionCancelRenewalFunctions = useMemo(
    () => createSubscriptionCancelRenewalHandler({
      history,
      questions,
      step,
      setStep,
      subscriptionAnalytics,
      subscriptions,
      subscriptionId: Number(subscriptionId),
      subscriptionType,
      stepsStatus: STEPS_STATUS,
      questionId,
      optionId,
      setSubscription,
      dispatch,
      openPreChatCancelFlow,
    }),
    [
      history, questions, step, setStep, subscriptionAnalytics, subscriptions,
      subscriptionId, subscriptionType, questionId, optionId, setSubscription,
      dispatch, openPreChatCancelFlow,
    ],
  );

  const getConfirmationProps = () => {
    const canShowCancelExclusionAddonVPSLabel = () => {
      const isVpsOCIAddon = HgProducts.VPS_OCI_ADDONS.includes(subscription.addonId);
      return isVpsOCIAddon;
    };

    const defaultProps = {
      title: confirmationLocale.confirmationTitleCancelRenewalLabel,
      attentionLabel: confirmationLocale.attentionLabel,
      backupFilesLabel: confirmationLocale.backupFilesLabel,
      backupFilesLinkLabel: confirmationLocale.backupFilesLinkLabel,
      stopCancelLabel: confirmationLocale.stopCancelRenewalLabel,
      canShowBackupLink: canShowBackupLink(),
      attentionCancelExclusionLabel: canShowCancelExclusionAddonVPSLabel() && confirmationLocale.attentionCancelExclusionLabel,
    };
    const onClickBackupLink = () => {
      subscriptionAnalytics.cancelSubscriptionClick('4_link_veja_como_fazer_backup_cancelar_renovacao');
      window.open(confirmationLocale.backupFilesLinkUrl, '_blank');
    };

    const onShowPageAction = () => {
      subscriptionAnalytics.cancelSubscriptionPageView('4_confirmar_cancelamento_renovacao');
    };

    if (!subscription && !subscription.dueDate) {
      return defaultProps;
    }

    const {
      type,
      cancelDate,
      dueDate,
    } = subscription;

    const getCancelledAddonsTopics = () => {
      if (subscription && subscription.addons) {
        return subscription.addons.map(addon => confirmationLocale.addonCancellation(addon, formatDate(dueDate)));
      }

      return [];
    };

    const getTopics = () => {
      if (type === 'domain') {
        return confirmationLocale.domainAttentionCancelRenewalTexts(formatDate(cancelDate));
      }
      if (type === 'addon' || !canShowBackupLink()) {
        return confirmationLocale.addonAttentionCancelRenewalTexts(formatDate(dueDate));
      }
      return confirmationLocale.attentionCancelRenewalTexts(formatDate(dueDate));
    };

    return {
      ...defaultProps,
      onStopCancel: () => subscriptionCancelRenewalFunctions.handleOnStopCancel(),
      attentionCancelSignatureLabel: getTopics(),
      topics: [...getCancelledAddonsTopics()],
      canShowBackupLink: canShowBackupLink(),
      onClickBackupLink,
      onShowPageAction,
    };
  };

  useEffect(() => {
    subscriptionAnalytics.cancelSubscriptionPageView('3_pesquisa_respondida_cancelar_renovacao');
    subscriptionAnalytics.cancelRenewalPageView();
  }, [subscriptionAnalytics]);

  useEffect(() => {
    if (redirect) {
      subscriptionCancelRenewalFunctions.redirect(redirect.url);
    }
  }, [redirect, subscriptionCancelRenewalFunctions]);

  useEffect(() => {
    if (!enabledRetentionOffer) {
      dispatch(billingActions.subscriptions.cancel.set({
        questionId: null,
        optionId: null,
        questionValue: '',
        reason: '',
      }));
    }
  }, [enabledRetentionOffer, dispatch]);

  useEffect(() => {
    if (!subscriptions.length) {
      dispatch(billingActions.subscriptions.request());
    }
  }, [subscriptions, dispatch]);

  useEffect(() => {
    if (subscriptions.length) {
      subscriptionCancelRenewalFunctions.findSubscription();
    }
  }, [subscriptions, subscriptionId, subscriptionCancelRenewalFunctions]);

  useEffect(() => {
    if (!questions[SubscriptionsQuestionTypes.CANCEL_RENEWAL]) {
      subscriptionCancelRenewalFunctions.requestQuestions();
    }
  }, [questions, subscriptionCancelRenewalFunctions]);

  useEffect(() => {
    if (subscriptions.length && !subscription) {
      subscriptionCancelRenewalFunctions.settingSubscriptions();
    }
  }, [subscriptions, subscription, subscriptionCancelRenewalFunctions]);

  useEffect(() => {
    modal && history.push(routesLocale.subscriptions);
  }, [history, modal, routesLocale.subscriptions]);


  const handleDeclineRenewDiscount = useCallback(() => {
    setStep(STEPS_STATUS.CONFIRMATION);
  }, [setStep]);

  if (loading || !subscription || !questions[SubscriptionsQuestionTypes.CANCEL_RENEWAL]) {
    return <Loader />;
  }

  const onFowardStepClick = () => {
    const actionSuccess = () => {
      setStep(STEPS_STATUS.NEXT_RENEW_OFFER);
    };

    const actionFail = () => {
      subscriptionCancelRenewalFunctions.onFowardStepClick();
    };

    if (enabledRetentionOffer && step < STEPS_STATUS.CONFIRMATION) {
      dispatch(billingActions.subscriptions.ableToCancelOffer.request(
        subscriptionId, subscriptionType, optionId, actionSuccess, actionFail,
      ));
    } else {
      subscriptionCancelRenewalFunctions.onFowardStepClick();
    }
  };

  return (
    <Styles.Wrapper data-testid="subscription-cancel-renewal-handler">
      <Styles.Header>
        <Header
          title={
            `${subscription.type === 'domain'
              ? billingLocale.domainLabel
              : subscription.productName} - `
          }
          description={subscription.domain}
        />
      </Styles.Header>
      <Styles.Content>
        <FeedbackStepper
          activeStep={step}
          steps={steps}
        />
        <Styles.StepContent>
          {step === STEPS_STATUS.NEXT_RENEW_OFFER && (
            <RetentionOfferPage handleDeclineRenewDiscount={handleDeclineRenewDiscount} />
          )}
          {
            step === STEPS_STATUS.SURVEY && (
              <Styles.StepSurveyContainer data-testid="subscription-cancel-renewal-step-survey">
                <CancelSurvey
                  testId="subscription-cancel-renewal-survey"
                  question={questions[SubscriptionsQuestionTypes.CANCEL_RENEWAL][0]}
                  selectedCategoryId={questionId}
                  selectedOptionId={optionId}
                  comment={reason}
                  onCategorySelect={subscriptionCancelRenewalFunctions.handleOnCategorySelect}
                  onOptionSelect={subscriptionCancelRenewalFunctions.handleOnSubOptionSelect}
                  onCommentChange={subscriptionCancelRenewalFunctions.handleOnCommentChange}
                />
              </Styles.StepSurveyContainer>
            )
          }
          {
            step === STEPS_STATUS.CONFIRMATION && (
              <Styles.StepConfirmationContainer data-testid="subscription-cancel-renewal-step-confirmation">
                <Confirmation {...getConfirmationProps()} />
              </Styles.StepConfirmationContainer>
            )
          }
          {
            step === STEPS_STATUS.DONE && (
              <Styles.StepDoneContainer data-testid="subscription-cancel-renewal-step-done">
                <OrderProcessing text={cancelLocale.processing} />
              </Styles.StepDoneContainer>
            )
          }
        </Styles.StepContent>

        {step !== STEPS_STATUS.NEXT_RENEW_OFFER && (
          <Styles.Controls disabled={step === STEPS_STATUS.DONE}>
            {
              step !== STEPS_STATUS.DONE && (
                <>
                  <Styles.ControlLeft>
                    <Button
                      label={confirmationLocale.controlBackButtonLabel}
                      iconLeft={<IconArrow direction="left" />}
                      onClick={() => subscriptionCancelRenewalFunctions.onPreviousStepClick()}
                      testId="subscription-cancel-renewal-back-button"
                      variant="secondary"
                      size="large"
                    />
                  </Styles.ControlLeft>
                  <Styles.ControlRight>
                    <Tooltip
                      width="270"
                      position="left"
                      text={confirmationLocale.tooltipCancelRenewalText}
                      disabled={!subscriptionCancelRenewalFunctions.isSurveyStepComplete()}
                    >
                      <Button
                        disabled={subscriptionCancelRenewalFunctions.isSurveyStepComplete()}
                        iconRight={step === STEPS_STATUS.CONFIRMATION ? null : <IconArrow direction="right" />}
                        label={
                          step === STEPS_STATUS.CONFIRMATION
                            ? confirmationLocale.controlFowardButtonStepConfirmationCancelRenewalLabel
                            : confirmationLocale.controlFowardButtonLabel
                        }
                        onClick={() => onFowardStepClick()}
                        testId="subscription-cancel-renewal-foward-button"
                        variant="primary"
                        size="large"
                      />
                    </Tooltip>
                  </Styles.ControlRight>
                </>
              )
            }
          </Styles.Controls>
        )}
      </Styles.Content>
    </Styles.Wrapper>
  );
};

export default CancelSubscriptionRenewalHandler;
